import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Badge, Alert } from 'react-bootstrap';

export const ProductPreview = ({ formData, onBack, onPublish }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showVideoModal, setShowVideoModal] = useState(false);

  // Get all images - prioritize coverImage, then check individual image fields and images array
  const getAllImages = () => {
    const imageList = [];
    
    // Add cover image first if it exists
    if (formData.coverImage && formData.coverImage.trim() !== '') {
      imageList.push(formData.coverImage);
    }
    
    // Add individual image fields only if they're different from cover image
    [formData.image1, formData.image2, formData.image3].forEach(img => {
      if (img && img.trim() !== '' && img !== formData.coverImage) {
        imageList.push(img);
      }
    });
    
    // Handle both old string format and new object format
    if (formData.images && Array.isArray(formData.images)) {
      formData.images.forEach(img => {
        let imagePath = null;
        
        // Check if it's an object (new format)
        if (typeof img === 'object' && img !== null) {
          imagePath = img.image; // Extract the 'image' property
        } 
        // Check if it's a string (old format)
        else if (typeof img === 'string') {
          imagePath = img;
        }
        
        // Add to list if valid and not duplicate
        if (imagePath && imagePath.trim() !== '' && !imageList.includes(imagePath)) {
          imageList.push(imagePath);
        }
      });
    }
    
    return imageList;
  };
  
  const allImages = getAllImages();

  // CRITICAL FIX: Updated getImageUrl to handle package/ prefix correctly
  const getImageUrl = (imageName) => {
    if (!imageName) return null;
    imageName = imageName.trim();
    
    // If it's already a full URL, return as-is
    if (imageName.startsWith('http://') || imageName.startsWith('https://')) {
      return imageName;
    }
    
    // If it starts with products/, use as-is
    if (imageName.startsWith('products/')) {
      return `https://s3.toolsvilla.com/${imageName}`;
    }
    
    // CRITICAL FIX: If it starts with package/, add products/ prefix
    if (imageName.startsWith('package/')) {
      return `https://s3.toolsvilla.com/products/${imageName}`;
    }
    
    // Default: assume it's a product image and add products/ prefix
    return `https://s3.toolsvilla.com/products/${imageName}`;
  };

  const getVideoUrl = () => {
    if (formData.videoFile) {
      return `https://media.toolsvilla.com/${formData.videoFile}`;
    }
    if (formData.videoUrl) {
      // Handle YouTube URLs
      const youtubeRegex = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/;
      const match = formData.videoUrl.match(youtubeRegex);
      if (match) {
        return `https://www.youtube.com/embed/${match[1]}`;
      }
      // Return original URL for other video formats
      return formData.videoUrl;
    }
    return null;
  };

  const isYouTubeVideo = () => {
    if (!formData.videoUrl) return false;
    return /(?:youtube\.com|youtu\.be)/.test(formData.videoUrl);
  };

  const playVideo = (videoSrc) => {
    if (videoSrc) {
      setShowVideoModal(true);
    }
  };

  // Helper function to check if any detail exists
  const hasManufacturerDetails = () => {
    return formData.manufacturerName || formData.manufacturerAddress || formData.manufacturerPincode;
  };

  const hasPackerDetails = () => {
    return formData.packerName || formData.packerAddress || formData.packerPincode;
  };

  const hasImporterDetails = () => {
    return formData.importerName || formData.importerAddress || formData.importerPincode;
  };

  return (
    <div className="bg-light min-vh-100">
      {/* Video Modal */}
      {showVideoModal && (
        <div className="video-modal-overlay" onClick={() => setShowVideoModal(false)}>
          <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="video-close-btn" onClick={() => setShowVideoModal(false)}>
              <i className="bi bi-x-circle-fill"></i>
            </button>
            <video
              src={getVideoUrl()}
              controls
              autoPlay
              className="modal-video"
            />
          </div>
        </div>
      )}

      <Container className="py-3">
        {/* Header */}
        <Row className="mb-3">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="h4 fw-bold text-dark mb-1">Product Preview</h1>
                <p className="text-muted mb-0 small">Review how your product will appear to customers</p>
              </div>
            </div>
          </Col>
        </Row>

        {/* Main Product Card */}
        <Card className="shadow-sm border-0">
          <Card.Body className="p-3">
            <Row className="g-3">
              {/* Product Images Section - REDUCED SIZE */}
              <Col lg={4}>
                {/* Main Image Display - Smaller */}
                <Card className="mb-2 border-0 shadow-sm">
                  <div className="ratio ratio-1x1 bg-light rounded-3 overflow-hidden" style={{ maxWidth: '350px', margin: '0 auto' }}>
                    {allImages.length > 0 ? (
                      <img 
                        src={getImageUrl(allImages[currentImageIndex])}
                        alt="Product" 
                        className="img-fluid"
                        style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                        onError={(e) => {
                          console.error('Failed to load image:', getImageUrl(allImages[currentImageIndex]));
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : (
                      <div className="d-flex align-items-center justify-content-center">
                        <div className="text-center">
                          <i className="bi bi-image-fill text-muted" style={{ fontSize: '2rem' }}></i>
                          <p className="text-muted mt-2 mb-0 small">No images uploaded</p>
                        </div>
                      </div>
                    )}
                    {/* Fallback for broken images */}
                    <div className="d-none align-items-center justify-content-center">
                      <div className="text-center">
                        <i className="bi bi-image-alt text-muted" style={{ fontSize: '2rem' }}></i>
                        <p className="text-muted mt-2 mb-0 small">Image not available</p>
                      </div>
                    </div>
                  </div>
                </Card>
                
                {/* Thumbnail Images - Smaller */}
                {allImages.length > 1 && (
                  <div style={{ maxWidth: '350px', margin: '0 auto' }}>
                    <Row className="g-2">
                      {allImages.slice(0, 4).map((image, index) => (
                        <Col key={index} xs={3}>
                          <Card 
                            className={`border-2 cursor-pointer ${currentImageIndex === index ? 'border-success' : 'border-light'}`}
                            onClick={() => setCurrentImageIndex(index)}
                          >
                            <div className="ratio ratio-1x1 bg-light rounded overflow-hidden">
                              <img 
                                src={getImageUrl(image)}
                                alt={`Thumbnail ${index + 1}`}
                                className="img-fluid"
                                style={{ objectFit: 'cover' }}
                                onError={(e) => {
                                  console.error('Failed to load thumbnail:', getImageUrl(image));
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <div className="d-none align-items-center justify-content-center">
                                <i className="bi bi-image text-muted"></i>
                              </div>
                            </div>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}

                {/* Video Section - Compact */}
                {(formData.videoFile || formData.videoUrl) && (
                  <Card className="mt-2 border-0 shadow-sm" style={{ maxWidth: '350px', margin: '0.5rem auto' }}>
                    <Card.Header className="py-2" style={{ backgroundColor: '#d4edda', color: '#155724' }}>
                      <h6 className="mb-0 fw-bold small">
                        <i className="bi bi-camera-video me-2"></i>
                        Product Video
                      </h6>
                    </Card.Header>
                    <Card.Body className="text-center p-2">
                      {isYouTubeVideo() ? (
                        <div className="ratio ratio-16x9">
                          <iframe
                            src={getVideoUrl()}
                            title="Product Video"
                            allowFullScreen
                            className="rounded-3"
                          ></iframe>
                        </div>
                      ) : (
                        <div className="bg-light p-2 rounded-3">
                          <i className="bi bi-camera-video mb-2" style={{ fontSize: '1.5rem', color: '#28a745' }}></i>
                          <p className="mb-2 small">Click to play product video</p>
                          <Button 
                            size="sm"
                            onClick={() => playVideo(getVideoUrl())}
                            style={{
                              backgroundColor: '#d4edda',
                              borderColor: '#c3e6cb',
                              color: '#155724'
                            }}
                          >
                            <i className="bi bi-play-fill me-2"></i>
                            Play Video
                          </Button>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                )}
              </Col>

              {/* Product Details Section */}
              <Col lg={8}>
                <div className="h-100">
                  {/* Product Title */}
                  <div className="mb-3">
                    <h2 className="h5 fw-bold text-dark mb-1">
                      {formData.productName || 'Product Name Not Specified'}
                    </h2>
                    <div className="text-muted small">
                      <i className="bi bi-tag me-2"></i>
                      {formData.selectedCategoryName || 'Uncategorized Product'}
                    </div>
                  </div>
                  
                  {/* Price and Stock Information */}
                  <Card className="mb-3 border-0 shadow-sm">
                    <Card.Header className="py-2" style={{ backgroundColor: '#d4edda', color: '#155724' }}>
                      <h6 className="mb-0 fw-bold small">
                        <i className="bi bi-currency-rupee me-2"></i>
                        Pricing & Inventory
                      </h6>
                    </Card.Header>
                    <Card.Body className="p-3">
                      <Row className="g-3">
                        <Col xs={6}>
                          <div className="text-center">
                            <div className="bg-light rounded p-2 mb-1">
                              <div className="text-muted mb-1 small fw-semibold">Transfer Price</div>
                              <h4 className="text-success fw-bold mb-0">₹{formData.transferPrice || '0.00'}</h4>
                              <small className="text-muted">+ GST {formData.gst || '0'}%</small>
                            </div>
                          </div>
                        </Col>
                        <Col xs={6}>
                          <div className="text-center">
                            <div className="bg-light rounded p-2 mb-1">
                              <div className="text-muted mb-1 small fw-semibold">Stock Available</div>
                              <h4 className="text-success fw-bold mb-0">{formData.stockCount || '0'}</h4>
                              <small className="text-muted">Units</small>
                            </div>
                          </div>
                        </Col>
                      </Row>
                      <hr className="my-2" />
                      <div className="row">
                        <div className="col-12">
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="fw-semibold text-muted small">Total Price (incl. GST):</span>
                            <span className="h6 text-success fw-bold mb-0">
                              ₹{formData.transferPrice && formData.gst ? 
                                (parseFloat(formData.transferPrice) + (parseFloat(formData.transferPrice) * parseFloat(formData.gst)) / 100).toFixed(2) : 
                                formData.transferPrice || '0.00'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>

                  {/* Basic Product Information */}
                  <Card className="mb-3 border-0 shadow-sm">
                    <Card.Header className="bg-white border-bottom py-2">
                      <h6 className="mb-0 fw-bold text-dark small">
                        <i className="bi bi-info-circle me-2 text-info"></i>
                        Basic Information
                      </h6>
                    </Card.Header>
                    <Card.Body className="p-3">
                      <Row className="g-2">
                        <Col xs={12}>
                          <div className="d-flex justify-content-between align-items-center py-2 px-2 bg-light rounded mb-2">
                            <span className="fw-semibold text-muted small d-flex align-items-center">
                              <i className="bi bi-building me-2"></i>Brand:
                            </span>
                            <span className="text-dark fw-bold small">{formData.brand || 'Not specified'}</span>
                          </div>
                        </Col>
                        
                        <Col xs={12}>
                          <div className="d-flex justify-content-between align-items-center py-2 px-2 bg-light rounded mb-2">
                            <span className="fw-semibold text-muted small d-flex align-items-center">
                              <i className="bi bi-globe me-2"></i>Country of Origin:
                            </span>
                            <span className="text-dark fw-bold small">{formData.countryOfOrigin || 'Not specified'}</span>
                          </div>
                        </Col>

                        <Col xs={12}>
                          <div className="d-flex justify-content-between align-items-center py-2 px-2 bg-light rounded mb-2">
                            <span className="fw-semibold text-muted small d-flex align-items-center">
                              <i className="bi bi-hash me-2"></i>HSN Code:
                            </span>
                            <span className="text-dark fw-bold small">{formData.hsnCode || 'Not specified'}</span>
                          </div>
                        </Col>

                        <Col xs={12}>
                          <div className="d-flex justify-content-between align-items-center py-2 px-2 bg-light rounded">
                            <span className="fw-semibold text-muted small d-flex align-items-center">
                              <i className="bi bi-percent me-2"></i>GST Rate:
                            </span>
                            <span className="text-dark fw-bold small">{formData.gst || '0'}%</span>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* Keywords */}
                  {formData.keywords && formData.keywords.length > 0 && (
                    <Card className="mb-3 border-0 shadow-sm">
                      <Card.Header className="bg-white border-bottom py-2">
                        <h6 className="mb-0 fw-bold text-dark small">
                          <i className="bi bi-tags me-2 text-warning"></i>
                          Product Keywords
                        </h6>
                      </Card.Header>
                      <Card.Body className="p-2">
                        <div className="d-flex flex-wrap gap-2">
                          {formData.keywords.map((keyword, index) => (
                            <Badge key={index} bg="secondary" className="px-2 py-1">
                              #{keyword}
                            </Badge>
                          ))}
                        </div>
                      </Card.Body>
                    </Card>
                  )}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Description Section */}
        <Card className="mt-3 shadow-sm border-0">
          <Card.Header className="bg-white border-bottom py-2">
            <h5 className="fw-bold mb-0 text-dark small">
              <i className="bi bi-file-text me-2" style={{ color: '#28a745' }}></i>
              Product Description
            </h5>
          </Card.Header>
          <Card.Body className="p-3">
            <Row className="g-3">
              <Col md={6}>
                <div className="h-100">
                  <h6 className="fw-semibold mb-2 small" style={{ color: '#28a745' }}>Short Description</h6>
                  <div className="bg-light p-3 rounded-3 h-100 d-flex align-items-start">
                    <p className="text-dark mb-0 small">
                      {formData.shortDescription || 'No short description provided'}
                    </p>
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="h-100">
                  <h6 className="fw-semibold mb-2 small" style={{ color: '#28a745' }}>Full Description</h6>
                  <div className="bg-light p-3 rounded-3 h-100 d-flex align-items-start">
                    <p className="text-dark mb-0 small" style={{ whiteSpace: 'pre-wrap' }}>
                      {formData.fullDescription || 'No detailed description provided'}
                    </p>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Manufacturer, Packer & Importer Details Section */}
        {(hasManufacturerDetails() || hasPackerDetails() || hasImporterDetails()) && (
          <Card className="mt-3 shadow-sm border-0">
            <Card.Header className="bg-white border-bottom py-2">
              <h5 className="fw-bold mb-0 text-dark small">
                <i className="bi bi-building me-2 text-info"></i>
                Manufacturer, Packer & Importer Details
              </h5>
            </Card.Header>
            <Card.Body className="p-3">
              <Row className="g-3">
                {/* Manufacturer Details */}
                {hasManufacturerDetails() && (
                  <Col md={4}>
                    <div className="h-100">
                      <div className="d-flex align-items-center mb-2">
                        <div className="bg-info text-white rounded-circle p-1 me-2" style={{ width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className="bi bi-building small"></i>
                        </div>
                        <h6 className="fw-bold text-info mb-0 small">Manufacturer Details</h6>
                      </div>
                      <div className="bg-light p-3 rounded-3">
                        {formData.manufacturerName && (
                          <div className="mb-2">
                            <div className="text-muted small mb-1">Name</div>
                            <div className="fw-bold text-dark small">{formData.manufacturerName}</div>
                          </div>
                        )}
                        {formData.manufacturerAddress && (
                          <div className="mb-2">
                            <div className="text-muted small mb-1">Address</div>
                            <div className="fw-semibold text-dark small">{formData.manufacturerAddress}</div>
                          </div>
                        )}
                        {formData.manufacturerPincode && (
                          <div>
                            <div className="text-muted small mb-1">Pincode</div>
                            <div className="fw-bold text-dark small">{formData.manufacturerPincode}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>
                )}

                {/* Packer Details */}
                {hasPackerDetails() && (
                  <Col md={4}>
                    <div className="h-100">
                      <div className="d-flex align-items-center mb-2">
                        <div className="bg-success text-white rounded-circle p-1 me-2" style={{ width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className="bi bi-box-seam small"></i>
                        </div>
                        <h6 className="fw-bold text-success mb-0 small">Packer Details</h6>
                        {formData.sameAsManufacturer && (
                          <Badge bg="secondary" className="ms-2">Same as Manufacturer</Badge>
                        )}
                      </div>
                      <div className="bg-light p-3 rounded-3">
                        {formData.packerName && (
                          <div className="mb-2">
                            <div className="text-muted small mb-1">Name</div>
                            <div className="fw-bold text-dark small">{formData.packerName}</div>
                          </div>
                        )}
                        {formData.packerAddress && (
                          <div className="mb-2">
                            <div className="text-muted small mb-1">Address</div>
                            <div className="fw-semibold text-dark small">{formData.packerAddress}</div>
                          </div>
                        )}
                        {formData.packerPincode && (
                          <div>
                            <div className="text-muted small mb-1">Pincode</div>
                            <div className="fw-bold text-dark small">{formData.packerPincode}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>
                )}

                {/* Importer Details */}
                {hasImporterDetails() && (
                  <Col md={4}>
                    <div className="h-100">
                      <div className="d-flex align-items-center mb-2">
                        <div className="bg-warning text-white rounded-circle p-1 me-2" style={{ width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className="bi bi-truck small"></i>
                        </div>
                        <h6 className="fw-bold text-warning mb-0 small">Importer Details</h6>
                      </div>
                      <div className="bg-light p-3 rounded-3">
                        {formData.importerName && (
                          <div className="mb-2">
                            <div className="text-muted small mb-1">Name</div>
                            <div className="fw-bold text-dark small">{formData.importerName}</div>
                          </div>
                        )}
                        {formData.importerAddress && (
                          <div className="mb-2">
                            <div className="text-muted small mb-1">Address</div>
                            <div className="fw-semibold text-dark small">{formData.importerAddress}</div>
                          </div>
                        )}
                        {formData.importerPincode && (
                          <div>
                            <div className="text-muted small mb-1">Pincode</div>
                            <div className="fw-bold text-dark small">{formData.importerPincode}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>
                )}
              </Row>

              {/* Show message if no details provided */}
              {!hasManufacturerDetails() && !hasPackerDetails() && !hasImporterDetails() && (
                <Alert variant="info" className="text-center mb-0 small">
                  <i className="bi bi-info-circle me-2"></i>
                  No manufacturer, packer, or importer details provided
                </Alert>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Specifications Section */}
        <Card className="mt-3 shadow-sm border-0">
          <Card.Header className="bg-white border-bottom py-2">
            <h5 className="fw-bold mb-0 text-dark small">
              <i className="bi bi-list-ul me-2 text-success"></i>
              Product Specifications
            </h5>
          </Card.Header>
          <Card.Body className="p-3">
            {/* Package Information */}
            {formData.packages && formData.packages.length > 0 ? (
              <div className="mb-3">
                <h6 className="fw-semibold text-success mb-2 small">
                  <i className="bi bi-box-seam me-2"></i>
                  Package Details {formData.packages.length > 1 && `(${formData.packages.length} Packages)`}
                </h6>
                <Row className="g-2">
                  {formData.packages.map((pkg, index) => (
                    <Col md={6} key={index}>
                      <Card className="border-2 border-success h-100">
                        <Card.Header className="bg-success text-white py-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <strong className="small">
                              {pkg.productName || `Package ${index + 1}`}
                            </strong>
                            {pkg.productName && (
                              <Badge bg="light" text="success" className="small">
                                <i className="bi bi-tag-fill me-1"></i>
                                Named Package
                              </Badge>
                            )}
                          </div>
                        </Card.Header>
                        <Card.Body className="p-2">
                          {/* Package Images */}
                          {pkg.images && pkg.images.length > 0 && (
                            <div className="mb-2">
                              <div className="text-muted small mb-1 fw-semibold">
                                <i className="bi bi-images me-1"></i>Package Images
                              </div>
                              <Row className="g-1">
                                {pkg.images.map((image, imgIndex) => (
                                  <Col xs={4} key={imgIndex}>
                                    <div className="position-relative">
                                      <div className="ratio ratio-1x1 bg-light rounded overflow-hidden">
                                        <img
                                          src={getImageUrl(image)}
                                          alt={`${pkg.productName || `Package ${index + 1}`} - Image ${imgIndex + 1}`}
                                          className="img-fluid"
                                          style={{ objectFit: 'cover' }}
                                          onError={(e) => {
                                            console.error('Failed to load package image:', getImageUrl(image));
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                          }}
                                        />
                                        <div className="d-none align-items-center justify-content-center">
                                          <i className="bi bi-image text-muted"></i>
                                        </div>
                                      </div>
                                      {pkg.imageUrl === image && (
                                        <Badge 
                                          bg="success" 
                                          className="position-absolute top-0 start-0 m-1" 
                                          style={{ fontSize: '0.6rem' }}
                                        >
                                          Main
                                        </Badge>
                                      )}
                                    </div>
                                  </Col>
                                ))}
                              </Row>
                            </div>
                          )}

                          {/* Package Weight */}
                          {pkg.weight && (
                            <div className="d-flex justify-content-between align-items-center py-1 px-2 mb-1 bg-light rounded">
                              <span className="fw-semibold text-muted small">
                                <i className="bi bi-box me-1"></i>Weight:
                              </span>
                              <span className="text-dark fw-bold small">{pkg.weight} {pkg.weightUnit || 'kg'}</span>
                            </div>
                          )}
                          
                          {/* Package Dimensions */}
                          {(pkg.length || pkg.width || pkg.height) && (
                            <div className="d-flex justify-content-between align-items-center py-1 px-2 mb-1 bg-light rounded">
                              <span className="fw-semibold text-muted small">
                                <i className="bi bi-rulers me-1"></i>Dimensions:
                              </span>
                              <span className="text-dark fw-bold small">
                                {pkg.length || 0} × {pkg.width || 0} × {pkg.height || 0} {pkg.dimensionUnit || 'cm'}
                              </span>
                            </div>
                          )}

                          {/* Empty state */}
                          {!pkg.productName && !pkg.images?.length && !pkg.weight && !pkg.length && !pkg.width && !pkg.height && (
                            <div className="text-center text-muted py-2">
                              <i className="bi bi-inbox" style={{ fontSize: '1.5rem' }}></i>
                              <p className="mb-0 small mt-1">No package details provided</p>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>
            ): (
              // Fallback to old single package format if packages array is empty
              formData.weight || formData.length || formData.width || formData.height ? (
                <div className="mb-3">
                  <h6 className="fw-semibold text-success mb-2 small">
                    <i className="bi bi-box-seam me-2"></i>
                    Package Details
                  </h6>
                  <Row>
                    <Col md={6}>
                      <Card className="border-2 border-success h-100">
                        <Card.Header className="bg-success text-white py-1">
                          <strong className="small">Package Information</strong>
                        </Card.Header>
                        <Card.Body className="p-2">
                          {formData.weight && (
                            <div className="d-flex justify-content-between align-items-center py-1 px-2 mb-1 bg-light rounded">
                              <span className="fw-semibold text-muted small">
                                <i className="bi bi-box me-1"></i>Weight:
                              </span>
                              <span className="text-dark fw-bold small">{formData.weight} {formData.weightUnit || 'kg'}</span>
                            </div>
                          )}
                          
                          {(formData.length || formData.width || formData.height) && (
                            <div className="d-flex justify-content-between align-items-center py-1 px-2 mb-1 bg-light rounded">
                              <span className="fw-semibold text-muted small">
                                <i className="bi bi-rulers me-1"></i>Dimensions:
                              </span>
                              <span className="text-dark fw-bold small">
                                {formData.length || 0} × {formData.width || 0} × {formData.height || 0} {formData.dimensionUnit || 'cm'}
                              </span>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </div>
              ) : (
                <Alert variant="info" className="text-center mb-3 small">
                  <i className="bi bi-info-circle me-2"></i>
                  No package specifications provided
                </Alert>
              )
            )}

            {/* Additional Specifications */}
            {formData.additionalSpecs && formData.additionalSpecs.length > 0 && (
              <div>
                <h6 className="fw-semibold text-success mb-2 small">
                  <i className="bi bi-gear me-2"></i>
                  Additional Specifications
                </h6>
                <Row className="g-2">
                  {formData.additionalSpecs.map((spec, index) => (
                    <Col md={6} key={index}>
                      <div className="d-flex justify-content-between align-items-center py-2 px-2 bg-light rounded">
                        <span className="fw-semibold text-muted small">{spec.name}:</span>
                        <span className="text-dark fw-bold small">{spec.value}</span>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Tax Information */}
        <Card className="mt-3 shadow-sm border-0">
          <Card.Header className="bg-white border-bottom py-2">
            <h5 className="fw-bold mb-0 text-dark small">
              <i className="bi bi-receipt me-2 text-warning"></i>
              Tax & Compliance Information
            </h5>
          </Card.Header>
          <Card.Body className="p-3">
            <Row className="g-3">
              <Col md={6}>
                <div className="bg-light p-3 rounded-3 h-100">
                  <h6 className="fw-semibold text-warning mb-2 small">GST Details</h6>
                  <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                    <span className="fw-semibold text-muted small">GST Rate:</span>
                    <span className="text-dark fw-bold small">{formData.gst || '0'}%</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center py-2 mt-2">
                    <span className="fw-semibold text-muted small">HSN Code:</span>
                    <span className="text-dark fw-bold small">{formData.hsnCode || 'Not specified'}</span>
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div className="bg-light p-3 rounded-3 h-100">
                  <h6 className="fw-semibold text-warning mb-2 small">Price Breakdown</h6>
                  <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                    <span className="fw-semibold text-muted small">Base Price:</span>
                    <span className="text-dark fw-bold small">₹{formData.transferPrice || '0.00'}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center py-2 mt-2">
                    <span className="fw-semibold text-muted small">GST Amount:</span>
                    <span className="text-dark fw-bold small">
                      ₹{formData.transferPrice && formData.gst ? 
                        ((parseFloat(formData.transferPrice) * parseFloat(formData.gst)) / 100).toFixed(2) : 
                        '0.00'}
                    </span>
                  </div>
                  <hr className="my-2" />
                  <div className="d-flex justify-content-between align-items-center py-2">
                    <span className="fw-bold text-dark small">Total Price:</span>
                    <span className="text-success fw-bold h6 mb-0">
                      ₹{formData.transferPrice && formData.gst ? 
                        (parseFloat(formData.transferPrice) + (parseFloat(formData.transferPrice) * parseFloat(formData.gst)) / 100).toFixed(2) : 
                        formData.transferPrice || '0.00'}
                    </span>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Action Buttons */}
        <Row className="mt-4">
          <Col className="text-center">
            <div className="d-flex justify-content-center gap-3">
              <Button 
                variant="outline-secondary" 
                size="sm" 
                onClick={onBack}
              >
                <i className="bi bi-pencil me-2"></i>
                Edit Product
              </Button>
              {onPublish && (
                <Button 
                  size="sm" 
                  onClick={onPublish}
                  style={{
                    backgroundColor: '#28a745',
                    borderColor: '#28a745',
                    color: '#fff'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#218838';
                    e.currentTarget.style.borderColor = '#1e7e34';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#28a745';
                    e.currentTarget.style.borderColor = '#28a745';
                  }}
                >
                  <i className="bi bi-check-circle me-2"></i>
                  Publish Product
                </Button>
              )}
            </div>
          </Col>
        </Row>
      </Container>

      <style jsx>{`
        .cursor-pointer {
          cursor: pointer;
        }
        
        .info-row .bg-light {
          transition: all 0.3s ease;
        }
        
        .info-row .bg-light:hover {
          background-color: #e9ecef !important;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .video-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.8);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1050;
        }
        
        .video-modal-content {
          position: relative;
          max-width: 90%;
          max-height: 90%;
        }
        
        .video-close-btn {
          position: absolute;
          top: -40px;
          right: -10px;
          background: none;
          border: none;
          color: white;
          font-size: 2rem;
          cursor: pointer;
          z-index: 1051;
        }
        
        .modal-video {
          max-width: 800px;
          max-height: 600px;
          width: 100%;
          height: auto;
          border-radius: 8px;
        }
        
        .card {
          transition: transform 0.2s ease-in-out;
        }
        
        .card:hover {
          transform: translateY(-2px);
        }
      `}</style>
    </div>
  );
};